import LazyLoadImage from '@repo/ui/src/components/LazyLoadImage';
import { ToutContent } from '@repo/ui/src/components/ToutContent';
import { HorizontalAlignmentEnum, VerticalAlignmentEnum } from '@repo/ui/src/enums/shared-enums';
import { imageFullWidthXs } from '@repo/ui/src/styles/variables';
import { classNames } from '@repo/ui/src/utils/classNames';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import { ImageProps, ToutContentProps } from '@repo/ui/src/types';
import Image from '@repo/ui/src/components/Image/New';

const bgClasses = {
  'Light Gray': 'tw-bg-warm-200',
  'Light Gray with Log': 'tw-bg-warm-200',
  Dark: 'tw-bg-neutral-700',
};

export const verticalAlignmentClasses = {
  [VerticalAlignmentEnum.Top]: 'tw-items-start',
  [VerticalAlignmentEnum.Center]: 'tw-items-center',
  [VerticalAlignmentEnum.Bottom]: 'tw-items-end',
};
export const horizontalAlignmentClasses = {
  [HorizontalAlignmentEnum.Left]: 'tw-justify-start',
  [HorizontalAlignmentEnum.Center]: 'tw-justify-center',
  [HorizontalAlignmentEnum.Right]: 'tw-justify-start',
};

interface SplitCardProps {
  toutContent: ToutContentProps;
  image: ImageProps;
  horizontalAlignment?: HorizontalAlignmentEnum;
  verticalAlignment?: VerticalAlignmentEnum;
  hasUnlimitedChars?: boolean;
  logBackground: boolean;
  splitLayoutBackground: keyof typeof bgClasses;
  _testID?: string;
  index?: number;
}

const SplitCard = ({
  toutContent,
  image,
  horizontalAlignment,
  verticalAlignment,
  hasUnlimitedChars,
  logBackground,
  splitLayoutBackground,
  _testID,
  index,
}: SplitCardProps) => {
  const logBackgroundImageUrl =
    'https://traegergrills.a.bigcontent.io/v1/static/log-cropped-transparent';

  const imageUrl = buildImageSrc(image, imageFullWidthXs, 1, 1);
  return (
    <div data-test-id={`split_card_${_testID}${index}`} className="tw-relative">
      <div className={classNames('tw-grid tw-auto-rows-fr md:tw-grid-cols-2 tw-h-full')}>
        {image?.image && (
          <div
            className={classNames(
              'tw-flex tw-content-center tw-items-center tw-flex-1 tw-h-auto tw-relative',
              horizontalAlignment === 'left' && 'md:tw-order-1',
            )}
          >
            <Image
              src={imageUrl}
              alt={image.image.name!}
              className="tw-w-full tw-h-full tw-object-cover"
              fill
            />
          </div>
        )}
        <div
          className={classNames(
            'tw-flex tw-flex-1 tw-h-auto tw-px-0',
            bgClasses[splitLayoutBackground],
            horizontalAlignment === 'right' && 'md:tw-pl-gutterSM',
            horizontalAlignment !== 'right' && 'md:tw-pr-gutterSM',
            horizontalAlignment
              ? horizontalAlignmentClasses[horizontalAlignment]
              : horizontalAlignmentClasses[HorizontalAlignmentEnum.Left],
            verticalAlignment
              ? verticalAlignmentClasses[verticalAlignment]
              : verticalAlignmentClasses[VerticalAlignmentEnum.Center],
          )}
        >
          {logBackground && (
            <LazyLoadImage
              imgComponent={
                <img
                  src={logBackgroundImageUrl}
                  alt=""
                  role="presentation"
                  className={classNames(
                    'tw-absolute tw-bottom-0 tw-w-[75%] tw-inset-x-[15%] tw-z-0 sm:tw-w-[35%] sm:tw-left-[8%] sm:tw-right-0 md:tw-w-[30%] md:tw-left-[10%]',
                    !splitLayoutBackground && 'tw-px-0',
                  )}
                />
              }
            />
          )}
          <ToutContent
            divider={!!toutContent.description}
            lineClamp={hasUnlimitedChars ? 4 : null}
            buttonNegative={splitLayoutBackground === 'Dark'}
            {...toutContent}
            className={classNames(
              'tw-p-6 tw-max-w-full tw-z-10 md:tw-p-gutterLG lg:tw-p-gutterSM md:tw-line-clamp-4 lg:tw-max-w-[80%] md:[&_p]:tw-line-clamp-4',
              !splitLayoutBackground && 'tw-px-0',
            )}
          />
        </div>
      </div>
    </div>
  );
};

export default SplitCard;
